/**
 * Role utility functions for proper role checking
 * Handles comma-separated role strings with exact matching
 */

export type RoleKey = 'user' | 'instructor' | 'courseadmin' | 'admin';

/**
 * Parse a comma-separated roles string into an array
 */
export function parseRoles(rolesStr: string | null | undefined): RoleKey[] {
  if (!rolesStr) return [];
  return rolesStr.split(',').map(r => r.trim()) as RoleKey[];
}

/**
 * Check if user has a specific role (exact match, not substring)
 */
export function hasRole(rolesStr: string | null | undefined, role: RoleKey): boolean {
  const roles = parseRoles(rolesStr);
  return roles.includes(role);
}

/**
 * Check if user has any of the specified roles (exact match)
 */
export function hasAnyRole(rolesStr: string | null | undefined, allowedRoles: RoleKey[]): boolean {
  const roles = parseRoles(rolesStr);
  return allowedRoles.some(r => roles.includes(r));
}

/**
 * German labels for roles
 */
export const ROLE_LABELS: Record<RoleKey, string> = {
  user: "Benutzer",
  instructor: "Kursleiter",
  courseadmin: "Kursadmin",
  admin: "Admin",
};

/**
 * Get display roles (hide 'user' when other roles exist)
 */
export function getDisplayRoles(rolesStr: string | null | undefined): RoleKey[] {
  const roles = parseRoles(rolesStr);
  if (roles.length > 1) {
    return roles.filter(r => r !== 'user');
  }
  return roles;
}

/**
 * Translate roles to German labels
 */
export function translateRoles(rolesStr: string | null | undefined): string {
  const displayRoles = getDisplayRoles(rolesStr);
  return displayRoles.map(r => ROLE_LABELS[r] || r).join(', ');
}
